import {
    Component,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChildren,
} from '@angular/core';
import { LegendButtons, AnatomicMapButtons, SegmentLabels, MultistageMapButtons } from '@se/cp.common';
import { LegendButtonsConf } from '../legend/LegendButtons';
import { LegendClickEvent } from '../legend/legendClickEvent';
import { MapComponent } from '../map/map.component';
import { IAnatomicMap, IMultistageAnatomicMap } from '../models/IAnatomicMap';
import { IColorCode } from '../models/IColorCode';
import { IColorData, IMultistageColorData } from '../models/IColorData';
import { IFixedSize } from '../models/IFixedSize';
import { IGuid } from '../models/IGuid';
import { IMatrixLayout } from '../models/IMatrixLayout';
import { IMultiStageContainerConfig } from '../models/IMultiStageContainerConfig';
import { ISegmentItem, RegionId } from '../models/ISegmentItem';
import { ISelectionEvent } from '../models/ISelectionEvent';
import { FillBoardService } from '../services/fillBoardService/fill-board.service';
import { ImgDownloaderService } from '../services/imgDownloaderService/img-downloader.service';
import { RasterImgWrapper } from '../services/imgDownloaderService/RasterImgWrapper';


export interface ISvgViews { svgView: SVGElement, stageID: string }

@Component({
    selector: 'multistage-map-container',
    templateUrl: './multistage-map-container.component.html',
    styleUrls: ['./multistage-map-container.component.scss'],
})
export class MultiStageMapContainerComponent implements OnInit, OnChanges {
    @Input()
    public multistageConfig: IMultiStageContainerConfig;

    @Input()
    public buttonsConfiguration: AnatomicMapButtons[] = [];

    _legendButton: LegendButtons[] = [];
    _multistage: MultistageMapButtons[] = [];

    @Input()
    public legendColors: Array<IColorCode>;

    @Input()
    public readOnly = false;

    @Input()
    public selectOnly: boolean = false;

    @Input()
    public oneLineDispositionMaps: boolean = false

    @Input()
    public segmentLabelType: SegmentLabels = SegmentLabels.none;

    @Input()
    public isHorizontal = false;

    @Input()
    public isWithTooltip = true;

    @Input()
    public isWithViewId = false;

    @Input()
    public isWithStageName = false;

    private _maxFixedColumnNumber: number;

    @Input()
    public fixedColumnNumber: number | null = null;

    @Input()
    public fixedLayout: {
        rows: number,
        columns: number
    } | null = null;

    @Input()
    public viewTopLabel: string | null = null;

    @Input()
    public viewBottomLabel: string | null = null;

    public viewsSideLenght = 61.5 * 3 + 10 + 4; // dimensione base della riga in SUITEESTENSA, impostiamo altezza minina 3 righe piu padding

    @Input()
    public fixedSize: IFixedSize = null;

    @Input()
    configuredRowNumber: number;

    @ViewChildren(MapComponent)
    private allMaps: MapComponent[];

    @Output()
    public dataUpdate = new EventEmitter<IMultistageColorData[]>();

    public currentColor: IColorCode;

    public defaultColor: IColorCode;

    private _selectedMap: IMultistageAnatomicMap = null;
    public get selectedMap() {
        return this._selectedMap;
    }
    public set selectedMap(currentMapSelected){
        this._selectedMap  = currentMapSelected;
        this.onExternalMapSelChange(currentMapSelected);
    }

    public selectedSegment: ISegmentItem = null;
    public selectedMapIndex: number | null = null;

    public buttonsConf: LegendButtonsConf = null;
    private mapsTilesLayout: IMatrixLayout;
    gridColumnsResponsiveLayout: string;
    gridRowResponsiveLayout: string;

    public get getLegendStyle() {
        if (this.isHorizontal) {
            return {
                'grid-row': '2',
                height: this.fixedSize ? this.fixedSize.height * 0.25 + 'px' : 'unset'
            };
        }
        else {
            return {
                'grid-column': '2',
            };
        }

    }

    public get getGridListStyle() {
        const gap = 7;
        if (this.oneLineDispositionMaps && !this.fixedColumnNumber) {
            return {
                display: 'grid',
                'box-sizing': 'border-box',
                'grid-template-columns': `auto`,
                'grid-template-rows': `auto`,
                'grid-auto-flow': 'column',
                padding: '0',
                gap: gap + 'px'
            }
        }
        if (!!this.fixedLayout) {
            return {
                display: 'grid',
                'box-sizing': 'border-box',
                'grid-template-columns': `repeat(${this.fixedLayout?.columns ?? 'auto-fill'},minmax(198.5px,1fr))`,
                'grid-template-rows': `repeat(${this.fixedLayout?.rows ?? 'auto-fill'},minmax(198.5px,1fr))`,
                'justify-content': 'unset',
                'overflow': 'auto',
                padding: '0',
                gap: gap + 'px'
            }
        }
        if (!!this.fixedColumnNumber && !!this._maxFixedColumnNumber) {
            return {
                display: 'grid',
                'box-sizing': 'border-box',
                'grid-template-columns': `repeat(${this._maxFixedColumnNumber},minmax(198.5px,1fr))`,
                'grid-template-rows': `repeat(auto-fit,minmax(${this.viewsSideLenght}px,1fr))`,
                'justify-content': 'unset',
                'overflow-x': 'auto',
                padding: '0',
                gap: gap + 'px'
            }
        } else {
            const maxRowDimension = this.configuredRowNumber ? this.configuredRowNumber * 61.5 - 28 + 'px' : '100%'
            const idealColGap = gap * (this.mapsTilesLayout.columnNumber - 1);
            const idealRowGap = gap * (this.mapsTilesLayout.rowNumber -1 ) ;
            const totalColGap = gap * (this.multistageConfig.maps.length - 1);
            const totalRowGap = gap * (this.multistageConfig.maps.length -1 ) ;
            const minValue = this.viewsSideLenght + 'px';
            const idealColValue = ` calc(100% / ${this.mapsTilesLayout.columnNumber}) - ${idealColGap}px`;
            const idealRowValue = ` calc( 100% / ${this.mapsTilesLayout.rowNumber}) - ${idealRowGap}px`;
            const maxColValue = ` calc(${maxRowDimension} - ${totalColGap}px)`;
            const maxRowValue = ` calc(${maxRowDimension} - ${totalColGap}px)`;
            this.gridColumnsResponsiveLayout = `repeat(auto-fill,minmax(clamp(${minValue},${idealColValue}, ${maxColValue}) ,1fr))`
            this.gridRowResponsiveLayout =  'repeat(auto-fit, minmax(198.5px, 1fr))'; //`repeat(auto-fill,minmax(clamp(${minValue},${idealRowValue}, ${maxRowValue}) ,1fr))`;

            return {
                display: 'grid',
                'box-sizing': 'border-box',
                'grid-template-columns': `${this.gridColumnsResponsiveLayout}`,
                'grid-template-rows': `${this.gridRowResponsiveLayout}`,
                'justify-content': 'start',
                padding: '0',
                gap: gap + 'px'
            }
        }
    }

    @HostBinding('style')
    get styles() {
        if (this.isHorizontal) {
            return {
                display: 'grid',
                'grid-template-rows': 'auto min-content',
                'gap': this.fixedSize?.gap ? this.fixedSize.gap + 'px' : '7px'
            };
        } else {
            return {
                display: 'grid',
                'grid-template-columns': 'auto min-content',
                'gap': this.fixedSize?.gap ? this.fixedSize.gap + 'px' : '7px'
            };
        }
    }

    viewUpperLabelFromMap(map: IAnatomicMap): string | null{
        return map.upperLabel ? map.upperLabel: this.viewTopLabel;
    }

    viewBottomLabelFromMap(map: IAnatomicMap): string | null{
        return map.lowerLabel ? map.lowerLabel : this.viewBottomLabel;
    }

    constructor(private fillBoardSrv: FillBoardService, private imgDownloader: ImgDownloaderService) { }

    public ngOnInit() {
        this.initLocalColors();
        this.initButtons(this.buttonsConfiguration as AnatomicMapButtons[]);
        // per scollegare i dati dal wrapper // cosa serve?
        // if (this.data) {
        //     this.data = [...this.data];
        // }

    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.multistageConfig) {
            if (this.selectedMapIndex != null && this.isSelectedMapNotChanged()) {
                this.multistageConfig.maps[this.selectedMapIndex].isVisited = true;
            }
            this.selectSegmentInAllView();
            this.deselectMapOnChangeStage();
        }
        if (changes.multistageConfig || changes.fixedSize || changes.fixedColumnNumber) {
            this.calcViewsSideL();
            this.calculateMaxGridDimension();
        }

        if (changes.buttonsConfiguration && !changes.buttonsConfiguration.isFirstChange) {
            this.initButtons(changes.buttonsConfiguration.currentValue as AnatomicMapButtons[]);
        }
    }

    private isSelectedMapNotChanged(): boolean {
        return this.multistageConfig != undefined
            && this.selectedMap != undefined
            && this.selectedMapIndex != undefined
            && this.multistageConfig.maps[this.selectedMapIndex].stageID === this.selectedMap.stageID;
    }
    private selectSegmentInAllView() {
        // se hai un segmento selezionato memorizzato
        // e se hai una mappa selezionata memorizzato
        // per tutte le mappe con lo stesso stageID della mappa selezionata
        // cerco nel modello se esiste un segmento con regionID uguale a quello del segmento selezionato
        // allora a questo segmento imposto is visited a true
        if (!!this.selectedSegment && !!this.selectedMap) {
            const sameStageIdMap: IMultistageAnatomicMap[] | undefined = this.multistageConfig.maps.filter((map: IMultistageAnatomicMap) => { return map.stageID === this.selectedMap.stageID })
            sameStageIdMap.forEach((map: IMultistageAnatomicMap) => {
                if (map.segmentCollection) {
                    const segmentToUpdate: ISegmentItem | undefined = map.segmentCollection.find((segment: ISegmentItem) => { return segment.regionID === this.selectedSegment.regionID });
                    if (!!segmentToUpdate) {
                        segmentToUpdate.isVisited = true;
                    }
                }
            })
        }
    }



    public trackByIdentity(index: number, map: IMultistageAnatomicMap) {
        // possibile miglioria eliminando stageID
        const mapIdentity = map.segmentCollection ? (map.stageID + map.viewId + map.segmentCollection.length +'map-index_'+ index) : map.stageID + map.viewId + 'empty'+'map-index_'+ index;
        return mapIdentity;
    }

    public onSelectionEvent(ev: ISelectionEvent, currentSelectedMap: IMultistageAnatomicMap, currentSelectedMapIndex: number): void {
        if (this.selectedMapIndex === currentSelectedMapIndex && this.selectedMap === currentSelectedMap && this.isSegmentAlreadySelected(ev?.segment)) {
            this.deselectSegment();
            return;
        }

        this.handleSegmentSelection(ev.segment);
        const isMapOrSegmentDeselected = ev.segment ? false : true;
        this.handleMapSelection(ev.originMap, isMapOrSegmentDeselected, currentSelectedMapIndex);
    }



    public onLegendSelection(code: IColorCode) {
        this.deselectSegment();
        this.currentColor = code;
    }

    public deselectSegment() {
        this.selectedSegment = null;
    }

    public deselectMap() {
        this.selectedMap = null;
        this.selectedMapIndex = null
    }

    public deselectMapOnChangeStage(){
        if(this.selectedMap && this.selectedMapIndex != null && this.multistageConfig && this.multistageConfig.maps){
            if(this.selectedMap.stageID !== this.multistageConfig.maps[this.selectedMapIndex!].stageID ||
                this.selectedMap.viewId !== this.multistageConfig.maps[this.selectedMapIndex!].viewId) {
                this.deselectMap();
            }
        }
    }

    public getAllViews(guids: IGuid[]): ISvgViews[] {
        return this.allMaps
            .filter((mapComponent: MapComponent) => {
                const isStagePrenset = guids.some((guid: IGuid) => {
                    return mapComponent.map.stageID === guid.stageID;
                });
                return !!isStagePrenset;
            })
            .map((mapComp: MapComponent) => {
                return {
                    svgView: mapComp.svgMap,
                    stageID: mapComp.map.stageID
                }
            });
    }

    // metodo esposto richiato nel wrapper per
    // generare il base64 delle immagini da inviare al server
    public getImages(guids: IGuid[]): Promise<RasterImgWrapper[]> {
        const toPrintSVGViews: ISvgViews[] = this.getAllViews(guids);
        if (!toPrintSVGViews || toPrintSVGViews.length === 0) {
            return Promise.resolve([]);
        }

        const singleMapPromise: Promise<RasterImgWrapper>[] = toPrintSVGViews.map((singleMap: ISvgViews) => {
            return this.imgDownloader
                .viewsImgCreator(singleMap.svgView, 'mappa', 1000, this.viewBottomLabel, singleMap.stageID);
        });

        return Promise.all([
            ...singleMapPromise,
            this.imgDownloader
                .legendImgCreator(this.legendColors, 'legenda', 400)
        ]);
    }

    private calcViewsSideL() {
        const numOfTiles = this.multistageConfig?.maps?.length || 0;
        if (this.fixedSize && !this.fixedColumnNumber) {
            const realWidth = this.fixedSize.width * (this.isHorizontal ? 1 : 0.75);
            const realHeight = this.fixedSize.height * (!this.isHorizontal ? 1 : 0.75);
            this.viewsSideLenght = this.fillBoardSrv.getTileDimension({
                numOfTiles: numOfTiles,
                boardW: realWidth,
                boardH: realHeight,
                padding: this.fixedSize.padding,
                gap: this.fixedSize.gap
            });
        } else {
            this.mapsTilesLayout = this.fillBoardSrv.getMatrixFromNum(numOfTiles);
        }
    }

    private handleSegmentSelection(eventSeg: ISegmentItem): void {
        if (this.isSegmentAlreadySelected(eventSeg)) {
            this.selectedSegment = null;
        } else {
            this.selectedSegment = { ...eventSeg };
            this.selectedSegment.isVisited = true;
        }
    }

    private handleMapSelection(eventMap: IMultistageAnatomicMap, isMapOrSegmentDeselected: boolean, currentSelectedMapIndex: number): void {
        if (this.isMapAlreadySelected(eventMap)
            && (!this.selectedSegment || isMapOrSegmentDeselected)) {
            this.selectedMapIndex = null;
            this.selectedMap = null;
        } else {
            this.selectedMapIndex = currentSelectedMapIndex;
            this.selectedMap = { ...eventMap };
            this.selectedMap.isVisited = true;
        }
    }

    public selectedSegmentForStage(currentMap: IMultistageAnatomicMap): ISegmentItem | null {
        if (!this.selectedMap?.stageID || currentMap.stageID !== this.selectedMap.stageID) {
            return null
        }
        return this.selectedSegment;
    }

    private isSegmentAlreadySelected(eventSeg: ISegmentItem): boolean {
        return !!eventSeg
            && !!this.selectedSegment
            && this.selectedSegment.regionID === eventSeg.regionID
            && eventSeg.isVisited;
    }

    private isMapAlreadySelected(eventMap: IMultistageAnatomicMap) {
        return !!eventMap
            && !!this.selectedMap
            && this.selectedMap.stageID === eventMap.stageID
            && this.selectedMap.viewId === eventMap.viewId
            && eventMap.isVisited
    }

    private calculateMaxGridDimension() {
        if (this.fixedColumnNumber) {
            this._maxFixedColumnNumber = Math.min(this.fixedColumnNumber, this.multistageConfig.maps.length);
        }
    }

    private columns(n: number): string {
        const base = '200px ';
        let result = '';
        while (n > 0) {
            result += base;
            n--;
        }
        return result.trim();
    }

    private hasPreviousMap(): boolean {
        return this.selectedMap && this.selectedMapIndex > 0 && this.selectedMapIndex < this.multistageConfig.maps.length
    }

    private getPreviousMap(): IMultistageAnatomicMap {
        return this.multistageConfig.maps[this.selectedMapIndex - 1]
    }

    public findColorDataByStageID(currentStageID: string): IMultistageColorData {
        return {
            ...this.multistageConfig.multistageColorData.find((stageColorData: IMultistageColorData) => {
                return stageColorData.stageID === currentStageID;
            })
        };
    }

    private initLocalColors() {
        if (!this.selectOnly && this.legendColors) {
            this.currentColor = this.legendColors[0];
            this.defaultColor = this.legendColors.filter(col => col.IsNormality)[0];
        }
    }

    private initButtons(btnconfig: AnatomicMapButtons[]) {

        this._multistage = Object.values(MultistageMapButtons).filter((value: string) => {
            return btnconfig.some((button: string) => { return button === value })
        });
        this._legendButton = Object.values(LegendButtons).filter((value: string) => {
            return btnconfig.some((button: string) => { return button === value });
        });

        this.buttonsConf = {
            normalColor: this.defaultColor,
            buttons: this._legendButton
        }


    }

    public onDataUpdated(currentSegmentData: IColorData, currentSelectedMap: IMultistageAnatomicMap) {
        if (!this.selectOnly) {
            const multistageColorData: IMultistageColorData = this.findColorDataByStageID(currentSelectedMap.stageID);
            const regionIndex: number = multistageColorData.colorData.findIndex((data) => data.regionID === currentSegmentData.regionID);
            multistageColorData.colorData[regionIndex] = { ...currentSegmentData };
            this.dataUpdate.emit([{ colorData: [currentSegmentData], stageID: currentSelectedMap.stageID }]);
        }
    }

    public onLegendBtnClick(ev: LegendClickEvent) {
        switch (ev) {
            case LegendClickEvent.uncolorAll:
                this.updateVisibleColoData(this.defaultColor);
                break;
            case LegendClickEvent.uncolorSelected:
                this.updateSelectedMapColorData(this.defaultColor);
                break;
            case LegendClickEvent.colorAll:
                this.updateVisibleColoData(this.currentColor);
                break;
            case LegendClickEvent.colorSelected:
                this.updateSelectedMapColorData(this.currentColor);
                break;
            default:
                break;
        }
    }


    private updateAllColorData(colorCodeToUpdate: IColorCode) {
        if (!this.selectOnly) {
            const stageIDToUpdate: string[] = this.filterStageToUpdate();
            const colorsDataToUpdate: IMultistageColorData[] = stageIDToUpdate.map((stageId: string) => {
                return this.findColorDataByStageID(stageId);
            });


            const colorDataUpdate: IMultistageColorData[] = colorsDataToUpdate.map((multistageColorData: IMultistageColorData) => {
                const newcolorData: IColorData[] = this.modifyAllColorData(multistageColorData.colorData, colorCodeToUpdate);
                return {
                    stageID: multistageColorData.stageID,
                    colorData: newcolorData
                }
            });
            this.dataUpdate.emit(colorDataUpdate);
        }
    }

    private updateVisibleColoData(colorCodeToUpdate: IColorCode) {
        // per ogni mappa ( non serve filtrare perche ora i segmenti sono diversi anche se stesso stageDI)
        // seleziona tutti i suoi region ID
        //
        // e nella mappa corrispettiva aggiorna i valori

        if (!this.selectOnly) {

            const colorDataUpdate: IMultistageColorData[] = this.multistageConfig.maps.reduce((toUpdateAccumulator: IMultistageColorData[], currentMap: IMultistageAnatomicMap) => {
                if (this.isMapValorized(currentMap)) {
                    const segmentToUpdate: ISegmentItem[] = currentMap.segmentCollection;
                    const colorDataToUpdate = this.multistageConfig.multistageColorData.find((colorData: IMultistageColorData) => {
                        return colorData.stageID === currentMap.stageID
                    }).colorData;
                    const colorDataToUpdated: IColorData[] = colorDataToUpdate.reduce((colorDatasToUpdate: IColorData[], colorData: IColorData) => {
                        if (this.isSegmentPresent(segmentToUpdate, colorData.regionID)) {
                            return colorDatasToUpdate.concat(colorData);
                        } else {
                            return colorDatasToUpdate;
                        }
                    }, []);
                    if (colorDataToUpdated.length > 0) {
                        return toUpdateAccumulator.concat({
                            stageID: currentMap.stageID,
                            colorData: this.modifyAllColorData(colorDataToUpdated,colorCodeToUpdate)
                        })
                    }
                } else {
                    return toUpdateAccumulator
                }
            }, [])
            this.dataUpdate.emit(colorDataUpdate);
        }
    }

    private isSegmentPresent(segmentItems: ISegmentItem[], regionID: RegionId): boolean {
        return segmentItems.some((segmentItem: ISegmentItem) => {
            return segmentItem.regionID === regionID;
        })
    }
    private updateSelectedMapColorData(colorCodeToUpdate: IColorCode) {

        if (this.selectedMap) {
            const stageIdOfSelectedMap: string = this.selectedMap.stageID;
            let regionIdDaAggiornare: RegionId[] = this.selectedMap.segmentCollection.map((segment: ISegmentItem) => {
                return segment.regionID;
            });
            const segmentoToUpdate: IColorData[] = this.multistageConfig.multistageColorData.filter((model: IMultistageColorData) => {
                return model.stageID === stageIdOfSelectedMap;
            })[0].colorData.filter((segment: IColorData) => {
                return regionIdDaAggiornare.includes(segment.regionID);
            });

            const stageColorData: IColorData[] = this.modifyAllColorData(segmentoToUpdate, colorCodeToUpdate);
            this.dataUpdate.emit([{
                colorData: stageColorData,
                stageID: stageIdOfSelectedMap
            }]);
        }
    }

    private filterStageToUpdate(): string[] {
        return this.multistageConfig.maps
            .filter((map: IMultistageAnatomicMap) => {
                return this.isMapValorized(map);
            })
            .map((map: IMultistageAnatomicMap) => {
                return map.stageID
            })
            .reduce((uniqueStageID: string[], stageId: string) => {
                return uniqueStageID.includes(stageId) ? uniqueStageID : [...uniqueStageID, stageId];
            }, []);
    }

    private modifyAllColorData(colorsCodeToModify: IColorData[], newColorCode: IColorCode): IColorData[] {
        return colorsCodeToModify.map((colorData: IColorData) => {
            const modifiedColorData: IColorData =  {
                regionID: colorData.regionID,
                segmentDescription: colorData.segmentDescription,
                ...newColorCode
            }
            return modifiedColorData;
        });
    }

    // private modifySegmentColorData(colorsCodeToModify: IColorData[], newColorCode: IColorCode):IColorData[] {
    //     return colorsCodeToModify.map((colorData: IColorData) =>{
    //         return colorData = {
    //              regionID : colorData.regionID,
    //              segmentDescription: colorData.segmentDescription,
    //              ...newColorCode
    //          }
    //      });
    // }

    public onCopyFromPrevious() {
        if (this.hasPreviousMap()) {
            const previousMap: IMultistageAnatomicMap = this.getPreviousMap();
            const previousMapColorData: IColorData[] = this.findColorDataByStageID(previousMap.stageID)?.colorData;
            if (previousMapColorData) {
                this.dataUpdate.emit([{ colorData: previousMapColorData, stageID: this.selectedMap.stageID }]);
            }
        }
    }

    public isMapValorized(map: IMultistageAnatomicMap): boolean {
        return !!map.segmentCollection;
    }

    _isThisMapSelected(map: IMultistageAnatomicMap): boolean {
        // versione multiStageMap
        if(this.selectedMap?.stageID != undefined && map.stageID != undefined){
            return this.selectedMap
            && map.stageID === this.selectedMap.stageID
            && map.viewId === this.selectedMap.viewId;
        }

        // versione mappa singola
        return this.selectedMap
            && map.viewId === this.selectedMap.viewId;
    }


       private onExternalMapSelChange(currentMapSelected: IAnatomicMap) {
        if(!currentMapSelected){
            return;
        }
        if (!this._selectedMap && !!currentMapSelected && currentMapSelected.isVisited) {
            currentMapSelected.isVisited = false;
            return;
        }
        if (this._isThisMapSelected(currentMapSelected)) {
            currentMapSelected.isVisited = true;
        } else {
            currentMapSelected.isVisited = false;
        }
    }
}
